import React, { useEffect, useState, useMemo } from "react";
import trash from "../../../../../assets/restaurant/images/trash.svg";

import {
    LOADER_TIMEOUT_TIME,
    ORDER_MANAGEMENT_STATUS,
    PAYMENT_OPTIONS,
    PAYMENT_STATUS,
    SORT_A,
    SORT_D,
    SWAL_SETTINGS,
} from "../../../../../utils/Constants";

import Swal from "sweetalert2";
import { useFormik } from "formik";
import { useValidationSchemas } from "../../../validator/validation";
import { useTranslation } from "react-i18next";
import {
    capitalizeFirstLetter,
    globalLoader,
    handleOffcanvasScroll,
    handleServerValidations,
    removeSpacesAndCharacters,
    removeSpecialCharacter,
    TrimText,
    useDebouncing,
    writeMyExcel,
} from "../../../../../utils/commonfunction";
import CustomError from "../../../../../utils/customError";
import RecordNotFound from "../../../shared/recordNotFound";
import TableActionDropdown from "../../../shared/components/TableActionDropdown";
import Skeleton from "react-loading-skeleton";
import logo from "../../../../../assets/restaurant/images/calenderLogo.png";
import * as Yup from "yup";
import { DatePicker, Drawer } from "rsuite";
import isBefore from "date-fns/isBefore";
import {
    addPayment,
    deletePaymentManagament,
    editPaymentManagament,
    paymentDetails,
    paymentListApi,
    paymentOrderOption,
    paymentVendorOption,
} from "../../../services/payment-managament.service";
import * as ProductManagementApi from "../../../services/product.managament.service"

const CustomBase64Caret = () => (
    <img src={logo} alt="Custom Caret" style={{ width: 20, height: 20 }} />
);

const ITEMS_PER_PAGE = 10;

const SimplePagination = ({ currentPage, totalRecords, itemsPerPage, onPageChange }) => {
    const totalPages = Math.ceil((Number(totalRecords) || 0) / itemsPerPage);

    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages = [];
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + 4);
        if (end - start < 4) start = Math.max(1, end - 4);
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    };

    return (
        <ul className="pagination mb-0">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button
                    className="page-link"
                    onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous"
                >
                    <span style={{ fontSize: "25px", opacity: "1.2" }} aria-hidden="true">
                        <i className="ti ti-chevron-left"></i>
                    </span>
                </button>
            </li>

            {getPageNumbers().map((pageNum) => (
                <li key={pageNum} className={`page-item ${currentPage === pageNum ? "active" : ""}`}>
                    <button className="page-link" onClick={() => onPageChange(pageNum)}>
                        {pageNum}
                    </button>
                </li>
            ))}

            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button
                    className="page-link"
                    onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next"
                >
                    <span style={{ fontSize: "25px", opacity: "1.2" }} aria-hidden="true">
                        <i className="ti ti-chevron-right"></i>
                    </span>
                </button>
            </li>
        </ul>
    );
};

export default function ProductManagement() {
    const [filters, setFilters] = useState({});
    const [details, setDetails] = useState(null);
    const [loader, setLoader] = useState(false);
    const [page, setPage] = useState(1);
    const { t } = useTranslation();
    const { editOrderValidationSchema } = useValidationSchemas();
    const [refreshList, setRefreshList] = useState(false);
    const [openWithHeader, setOpenWithHeader] = useState(false);
    const [openWithHeaderEdit, setOpenWithHeaderEdit] = useState(false);
    const [vendorOptions, setVendorOptions] = useState([]);
    const [orderOptionList, setOrderOptionList] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        column: "createdAt",
        order: SORT_D,
    });
    const [addSubmissionLoader, setAddSubmissionLoader] = useState(false);
    const [editSubmissionLoader, setEditSubmissionLoader] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(true);

    const [productListData, setProductListData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [offsetPage, setOffsetPage] = useState(0);

    const [searchValue, setSearchValue] = useState("");
    const [globalSearch, setGlobalSearch] = useState("");
    const [filterDate, setFilterDate] = useState(null);

    // ✅ NEW: Status filter state - "" means all, "active" or "deactive"
    const [statusFilter, setStatusFilter] = useState("");

    const prepareGlobalSearch = (value) => {
        setGlobalSearch(value);
        setPage(1);
    };
    const searchDebounce = useDebouncing(prepareGlobalSearch, 500);

    const handleSearchChange = (e) => {
        setSearchValue(e.target.value);
        searchDebounce(e.target.value);
    };

    // ✅ NEW: Status filter change handler
    const handleStatusFilterChange = (e) => {
        setStatusFilter(e.target.value);
        setPage(1);
    };

    const handleClearDateFilter = () => {
        setFilterDate(null);
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    useEffect(() => {
        const formData = new FormData();
        paymentOrderOption(formData)
            .then((response) => {
                if (response?.data) {
                    setOrderOptionList(response?.data?.list || []);
                }
            })
            .catch((error) => {
                console.log("Order list error ====> ", error);
            });
    }, []);

    useEffect(() => {
        setLoader(true);
        const formData = new FormData();
        if (searchValue?.trim() || Object.keys(filters).length) {
            formData.append("searchValue", searchValue.trim());
        }
        formData.append("global_search", globalSearch);
        formData.append("page", page);
        formData.append("per_page", ITEMS_PER_PAGE);
        if (sortConfig) formData.append("sort", JSON.stringify(sortConfig));

        // ✅ NEW: Send status filter to API if selected
        if (statusFilter) {
            formData.append("status", statusFilter);
        }

        ProductManagementApi
            .list(formData)
            .then((response) => {
                if (response?.data) {
                    setProductListData(response.data.list || []);
                    setTotalRecords(response.data.total_records || 0);
                    setOffsetPage(response.data.offset || 0);
                } else {
                    setProductListData([]);
                    setTotalRecords(0);
                    setOffsetPage(0);
                }
                setTimeout(() => setLoader(false), LOADER_TIMEOUT_TIME);
            })
            .catch((error) => {
                console.log("API Error ===>", error);
                setProductListData([]);
                setTotalRecords(0);
                setLoader(false);
            });
    }, [page, globalSearch, sortConfig, refreshList, filters, statusFilter]); // ✅ statusFilter added in dependency

    const formatOrderId = (id) => {
        if (!id) return "-";
        const lastSix = id.slice(-6);
        return parseInt(lastSix, 16);
    };

    const displayData = useMemo(() => {
        let data = productListData;

        // ✅ Frontend status filter
        if (statusFilter) {
            data = data.filter((item) => item?.status === statusFilter);
        }

        // Date filter
        if (filterDate) {
            const selectedDate = new Date(filterDate);
            selectedDate.setHours(0, 0, 0, 0);
            data = data.filter((item) => {
                if (!item?.payment_date) return false;
                const itemDate = new Date(item.payment_date);
                itemDate.setHours(0, 0, 0, 0);
                return itemDate.getTime() === selectedDate.getTime();
            });
        }

        return data;
    }, [productListData, filterDate, statusFilter]);

    const handleViewDetails = async (item) => {
        try {
            setDetailsLoading(true);
            const response = await ProductManagementApi.Details({ o_id: item._id });
            if (response?.success) {
                setDetails(response?.data);
            } else if (response?.data) {
                setDetails(response?.data);
            }
            setDetailsLoading(false);
        } catch (err) {
            console.error(err);
            setDetailsLoading(false);
        }
    };

    const addPaymentValidationSchema = Yup.object().shape({
        name: Yup.string()
            .trim()
            .matches(/^[A-Za-z0-9][A-Za-z0-9\s]*$/, "Invalid format")
            .required("Name is required"),

        price: Yup.number()
            .typeError("Price must be a number")
            .required("Price is required")
            .moreThan(0, "Price cannot be 0 or negative")
            .max(100000, "Price cannot be more than 100000"),

        description: Yup.string()
            .trim()
            .matches(/^[A-Za-z0-9].*$/, "Invalid format")
            .required("Description is required"),

        status: Yup.string()
            .required("Status is required"),
    });

    const editProductValidationSchema = Yup.object().shape({
        _id: Yup.string().required("Product ID is required"),
        name: Yup.string()
            .trim()
            .matches(/^[A-Za-z0-9][A-Za-z0-9\s]*$/, "Invalid format")
            .required("Name is required"),
        price: Yup.number()
            .required("Price is required")
            .moreThan(0, "Price cannot be 0 or negative")
            .max(100000, "Price cannot be more than 100000"),
        description: Yup.string().required("Description is required"),
        status: Yup.string().required("Status is required"),
    });

    const handleEditDetails = async (item) => {
        try {
            setDetailsLoading(true);

            const response = await ProductManagementApi.Details({ o_id: item._id });

            if (response?.success || response?.data) {
                const data = response?.data;

                editForm.setValues({
                    _id: data?._id || "",
                    name: data?.name || "",
                    price: data?.price || "",
                    description: data?.description || "",
                    status: data?.status || "",
                });
            }

            setDetailsLoading(false);
        } catch (err) {
            console.error("Error loading product for edit:", err);
            setDetailsLoading(false);
        }
    };

    const deletePaymentRecord = (item) => {
        Swal.fire({
            title: t("msg_are_you_sure"),
            text: t("btn_delete_text"),
            imageUrl: trash,
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: t("btn_delete"),
            cancelButtonText: t("btn_cancel"),
            reverseButtons: true,
            customClass: {
                popup: "swal-restaurant",
                confirmButton: "btn btn-danger col",
                cancelButton: "btn btn-secondary col",
            },
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                globalLoader(true);
                const formData = new FormData();
                formData.append("_id", item._id);
                ProductManagementApi.deleteProduct(formData)
                    .then((response) => {
                        globalLoader(false);
                        if (response.success) {
                            Swal.fire({
                                icon: "success",
                                text: response.message,
                                ...SWAL_SETTINGS,
                            });

                            setRefreshList((prev) => !prev);
                        } else {
                            Swal.fire({
                                icon: "error",
                                text: handleServerValidations(response),
                                ...SWAL_SETTINGS,
                            });
                        }
                    })
                    .catch((error) => {
                        globalLoader(false);
                        console.error("deleteProductError", error);
                    });
            }
        });
    };

    const addForm = useFormik({
        initialValues: {
            name: "",
            price: "",
            description: "",
            status: "active",
            createdBy: "",
            updatedBy: ""
        },
        validationSchema: addPaymentValidationSchema,
        enableReinitialize: true,
        onSubmit: (values, { resetForm }) => {
            const formData = new FormData();
            formData.append("name", values.name);
            formData.append("price", values.price);
            formData.append("description", values.description);
            formData.append("status", values.status);
            setAddSubmissionLoader(true);
            ProductManagementApi.add(formData)
                .then((response) => {
                    setAddSubmissionLoader(false);
                    if (response.success) {
                        Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
                        resetForm();
                        setPage(1);
                        setOpenWithHeader(false);
                        setRefreshList(!refreshList);
                        setSortConfig({ column: "createdAt", order: SORT_D });
                    } else {
                        Swal.fire({ icon: "error", text: handleServerValidations(response), ...SWAL_SETTINGS });
                    }
                })
                .catch((error) => {
                    setAddSubmissionLoader(false);
                    console.error("Error adding product", error);
                });
        },
    });

    const editForm = useFormik({
        initialValues: {
            _id: "",
            name: "",
            price: "",
            description: "",
            status: "",
        },
        validationSchema: editProductValidationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            setEditSubmissionLoader(true);

            const formData = new FormData();
            formData.append("_id", values._id);
            formData.append("name", values.name);
            formData.append("price", values.price);
            formData.append("description", values.description);
            formData.append("status", values.status);

            ProductManagementApi.editProduct(formData)
                .then((response) => {
                    setEditSubmissionLoader(false);
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS
                        });
                        setOpenWithHeaderEdit(false);
                        setRefreshList(!refreshList);
                        editForm.resetForm();
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS
                        });
                    }
                })
                .catch((error) => {
                    setEditSubmissionLoader(false);
                    console.error("Error updating product", error);
                });
        },
    });

    const handleOrderExportToExcel = () => {
        if (!productListData || productListData.length === 0) return;
        const headers = [
            t("label_name"),
            t("label_price"),
            t("label_status"),
        ];
        const rows = productListData.map((item) => [
            item?.name || "-",
            item?.price || "-",
            item?.status || "-",
        ]);
        writeMyExcel([headers, ...rows], `${t("label_product_list")}.xlsx`);
    };

    const vendnorOptionHandler = (orderId, formInstance = addForm) => {
        const formData = new FormData();
        formData.append("order_id", orderId);
        paymentVendorOption(formData)
            .then((response) => {
                const vendors = Array.isArray(response.data) ? response.data : [response.data];
                setVendorOptions(vendors);
                if (vendors.length > 0) {
                    formInstance.setFieldValue("vendor_id", vendors[0].vendor_id?.toString());
                    formInstance.setFieldValue("payment_amount", vendors[0].order_amount || "");
                }
            })
            .catch((error) => console.error("Error fetching vendor options:", error));
    };

    const formatPaymentLabel = (value) => {
        if (!value) return "";
        if (value.toLowerCase() === "upi") return "UPI";
        return value
            .toLowerCase()
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    const handleSort = (key) => {
        let direction = SORT_A;
        if (sortConfig.column === key && sortConfig.order === SORT_A) {
            direction = SORT_D;
        }
        setSortConfig({ column: key, order: direction });
        setPage(1);
    };

    const showingCount =
        totalRecords > 0
            ? Math.min(page * ITEMS_PER_PAGE, totalRecords)
            : 0;

    return (
        <main id="main-content">
            <div className="inner-container">
                <div className="page-header d-flex align-items-center">
                    <h4 className="heading-title-text">{t("label_product_management")}</h4>
                </div>

                <div className="card-outer-header d-flex justify-content-between align-items-center mb-4">
                    {/* ✅ Search + Status Filter side by side */}
                    <div className="d-flex align-items-center gap-2">
                        <div className="search-box">
                            <input
                                type="text"
                                value={searchValue}
                                className="form-control search-input"
                                placeholder={t("input_placeholder_search")}
                                onChange={handleSearchChange}
                            />
                        </div>

                        {/* ✅ NEW: Status Filter Dropdown */}
                        <div>
                            <select
                                className="form-control cp"
                                value={statusFilter}
                                onChange={handleStatusFilterChange}
                                style={{ minWidth: "140px" }}
                            >
                                <option value="">{t("label_all") || "All Status"}</option>
                                <option value="active">{t("label_active") || "Active"}</option>
                                <option value="deactive">{t("label_deactive") || "Inactive"}</option>
                            </select>
                        </div>
                    </div>

                    <div className="card-head-group d-flex align-items-center gap-2">
                        <button className="btn btn-primary" onClick={() => setOpenWithHeader(true)}>
                            <i className="ti ti-plus"></i> {t("label_product_registration")}
                        </button>

                        <button
                            className="btn btn-primary"
                            style={{ whiteSpace: "nowrap" }}
                            onClick={handleOrderExportToExcel}
                            disabled={productListData.length === 0}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14 3V7C14 7.26522 14.1054 7.51957 14.2929 7.70711C14.4804 7.89464 14.7348 8 15 8H19M14 3H7C6.46957 3 5.96086 3.21071 5.58579 3.58579C5.21071 3.96086 5 4.46957 5 5V19C5 19.5304 5.21071 20.0391 5.58579 20.4142C5.96086 20.7893 6.46957 21 7 21H11.5M14 3L19 8M19 8V13M14 19H21M21 19L18 16M21 19L18 22" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>{" "}
                            {t("label_export")}
                        </button>
                    </div>
                </div>

                <div className="page-header d-flex align-items-center">
                    <h6 className="heading-title-text">{t("label_product_history")}</h6>
                </div>

                <div className="dashboard-content-wraper">
                    <div className="category-list-wrap">
                        {loader ? (
                            <div className="w-100">
                                <Skeleton className="mb-2" height={50} count={5} />
                            </div>
                        ) : displayData?.length ? (
                            <div className="card">
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        {t("label_procuct_id")}
                                                        <span style={{ cursor: "pointer" }} onClick={() => handleSort("orderId")}>
                                                            <i className={`${sortConfig.column === "orderId" && sortConfig.order === SORT_D ? "sort-disabled" : ""}`} style={{ marginLeft: "5px" }}></i>
                                                        </span>
                                                    </th>
                                                    <th>
                                                        {t("label_name")}
                                                        <span style={{ cursor: "pointer" }} onClick={() => handleSort("companyName")}>
                                                            <i className={`${sortConfig.column === "companyName" && sortConfig.order === SORT_D ? "sort-disabled" : ""}`} style={{ marginLeft: "5px" }}></i>
                                                        </span>
                                                    </th>
                                                    <th>{t("label_price")}</th>
                                                    <th>{t("label_status")}</th>
                                                    <th>{t("label_action")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {displayData.map((item) => (
                                                    <tr key={item._id}>
                                                        <td>#{formatOrderId(item._id)}</td>
                                                        <td>{capitalizeFirstLetter(TrimText(item && item?.name, 20)) || "-"}</td>
                                                        <td>{capitalizeFirstLetter(TrimText(`¥${item && item?.price}`)) || "-"}</td>
                                                        <td>
                                                            {item?.status === "active" ? (
                                                                <span className="status-badge d-block status-badge-success" style={{ width: "max-content" }}>
                                                                    {t("label_active")}
                                                                </span>
                                                            ) : (
                                                                <span className="status-badge d-block status-badge-danger" style={{ width: "max-content" }}>
                                                                    {t("label_deactive")}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="">
                                                            <TableActionDropdown>
                                                                    <li>
                                                                        <a
                                                                            className="dropdown-item cp"
                                                                            onClick={() => handleViewDetails(item)}
                                                                            data-bs-toggle="offcanvas"
                                                                            data-bs-target="#view-staff"
                                                                        >
                                                                            <i className="ti ti-eye"></i>
                                                                            {t("btn_view")}
                                                                        </a>
                                                                    </li>
                                                                    <li>
                                                                        <a
                                                                            className="dropdown-item cp"
                                                                            onClick={() => {
                                                                                handleEditDetails(item);
                                                                                setOpenWithHeaderEdit(true);
                                                                            }}
                                                                        >
                                                                            <i className="ti ti-edit"></i> {t("btn_edit")}
                                                                        </a>
                                                                    </li>
                                                                    <li>
                                                                        <a className="dropdown-item cp text-danger" onClick={() => deletePaymentRecord(item)}>
                                                                            <i className="ti ti-trash"></i> {t("btn_delete")}
                                                                        </a>
                                                                    </li>
                                                            </TableActionDropdown>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="table-status-info d-flex justify-content-between align-items-center">
                                        <span>
                                            {t("label_showing_entries", { shown: showingCount, total: totalRecords })}
                                        </span>
                                        <nav aria-label="Page navigation example">
                                            <SimplePagination
                                                currentPage={page}
                                                totalRecords={totalRecords}
                                                itemsPerPage={ITEMS_PER_PAGE}
                                                onPageChange={handlePageChange}
                                            />
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <RecordNotFound heading="no_prodcut_found" />
                        )}
                    </div>
                </div>
            </div>

            {/* Add Product Drawer */}
            <Drawer open={openWithHeader}>
                <Drawer.Header closeButton={false}>
                    <div className="d-flex justify-content-between w-100 align-items-center">
                        <h5 className="mb-0">{t("label_product_registration")}</h5>
                        <button className="btn-icon" onClick={() => { setOpenWithHeader(false); addForm.resetForm(); }}>
                            <i className="ri-close-fill"></i>
                        </button>
                    </div>
                </Drawer.Header>
                <Drawer.Body onScroll={(e) => handleOffcanvasScroll(e, "editNewEmail", "rsuite")}>
                    <form onSubmit={addForm.handleSubmit}>
                        <div className="mb-3">
                            <label className="form-label">{t("label_name")}<span>*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("label_product_name")}
                                name="name"
                                value={addForm.values.name}
                                onChange={addForm.handleChange}
                            />
                            <CustomError name="name" form={addForm} className="text-danger" />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">{t("label_price")}<span>*</span></label>
                            <input
                                type="number"
                                className="form-control"
                                placeholder={t("label_enter_price")}
                                name="price"
                                value={addForm.values.price}
                                onChange={addForm.handleChange}
                            />
                            <CustomError name="price" form={addForm} className="text-danger" />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">{t("label_description")}  <span>*</span></label>
                            <textarea
                                type="text"
                                className="form-control"
                                style={{ height: "120px" }}
                                placeholder={t("label_enter_description")}
                                name="description"
                                value={addForm.values.description}
                                onChange={addForm.handleChange}
                            />
                            <CustomError name="description" form={addForm} className="text-danger" />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">{t("label_vendor_status")}</label>
                            <select
                                className="form-control cp"
                                name="status"
                                value={addForm?.values?.status || ""}
                                onChange={addForm.handleChange}
                            >
                                <option disabled value="">{t("input_select_text")}</option>
                                <option value="active">{t("label_active")}</option>
                                <option value="deactive">{t("label_deactive")}</option>
                            </select>
                            <CustomError name="status" form={addForm} className="text-danger" />
                        </div>

                        <div className="button-form">
                            <button type="submit" className={"btn btn-primary w-100" + (addSubmissionLoader ? " btn-loader" : "")}>
                                {t("label_vendor_save")}
                            </button>
                        </div>
                    </form>
                </Drawer.Body>
            </Drawer>

            {/* Edit Product Drawer */}
            <Drawer open={openWithHeaderEdit}>
                <Drawer.Header closeButton={false}>
                    <div className="d-flex justify-content-between w-100 align-items-center">
                        <h5 className="mb-0">{t("edit_prodcut")}</h5>
                        <button className="btn-icon" onClick={() => { setOpenWithHeaderEdit(false); editForm.resetForm(); }}>
                            <i className="ri-close-fill"></i>
                        </button>
                    </div>
                </Drawer.Header>
                <Drawer.Body>
                    {detailsLoading ? (
                        <Skeleton className="mb-2" height={30} count={5} />
                    ) : (
                        <form onSubmit={editForm.handleSubmit}>
                            <input type="hidden" name="_id" value={editForm.values._id} />

                            <div className="mb-3">
                                <label className="form-label">{t("label_name")}<span>*</span></label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t("label_enter_name")}
                                    name="name"
                                    value={editForm.values.name}
                                    onChange={editForm.handleChange}
                                    onBlur={editForm.handleBlur}
                                />
                                <CustomError name="name" form={editForm} className="text-danger" />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">{t("label_price")}<span>*</span></label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder={t("label_enter_price")}
                                    name="price"
                                    value={editForm.values.price}
                                    onChange={editForm.handleChange}
                                    onBlur={editForm.handleBlur}
                                />
                                <CustomError name="price" form={editForm} className="text-danger" />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">{t("label_description")}<span>*</span></label>
                                <textarea
                                    type="text"
                                    className="form-control"
                                    style={{ height: "120px" }}
                                    placeholder={t("label_enter_description")}
                                    name="description"
                                    value={editForm.values.description}
                                    onChange={editForm.handleChange}
                                    onBlur={editForm.handleBlur}
                                />
                                <CustomError name="description" form={editForm} className="text-danger" />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">{t("label_status")}</label>
                                <select
                                    className="form-control"
                                    name="status"
                                    value={editForm.values.status}
                                    onChange={editForm.handleChange}
                                    onBlur={editForm.handleBlur}
                                >
                                    <option value="">{t("input_select_text")}</option>
                                    <option value="active">{t("label_active")}</option>
                                    <option value="deactive">{t("label_deactive")}</option>
                                </select>
                                <CustomError name="status" form={editForm} className="text-danger" />
                            </div>

                            <div className="button-form">
                                <button
                                    type="submit"
                                    className={"btn btn-primary w-100" + (editSubmissionLoader ? " btn-loader" : "")}
                                >
                                    {t("btn_text_update")}
                                </button>
                            </div>
                        </form>
                    )}
                </Drawer.Body>
            </Drawer>

            {/* View Details Offcanvas */}
            <div className="offcanvas offcanvas-end right-modal-w" id="view-staff" data-bs-backdrop="static" tabIndex="-1" aria-labelledby="staticBackdropLabel" aria-modal="true" role="dialog">
                <div className="offcanvas-header">
                    <h5 className="offcanvas-title">{t("product_details")}</h5>
                    <button type="button" className="btn btn-icon ms-auto" data-bs-dismiss="offcanvas" aria-label="Close">
                        <i className="ti ti-x"></i>
                    </button>
                </div>
                <div className="offcanvas-body">
                    {detailsLoading ? (
                        <Skeleton className="mb-2" height={30} count={5} />
                    ) : details ? (
                        <div className="view-staff-info">
                            <div className="row gap-4">
                                <div className="col-md-12">
                                    <h4>{t("label_procuct_id")}</h4>
                                    <p>{details._id ? `#${formatOrderId(details._id)}` : t("label_not_entered")}</p>
                                </div>
                                <div className="col-md-12">
                                    <h4>{t("label_name")}</h4>
                                    <p>{details.name || t("label_not_entered")}</p>
                                </div>
                                <div className="col-md-12">
                                    <h4>{t("label_price")}</h4>
                                    <p>{details.price !== undefined ? `¥${details.price}` : t("label_not_entered")}</p>
                                </div>
                                <div className="col-md-12">
                                    <h4>{t("label_description")}</h4>
                                    <p>{details.description || t("label_not_entered")}</p>
                                </div>
                                <div className="col-md-12">
                                    <h4>{t("label_status")}</h4>
                                    <p className="text-capitalize">{details.status || t("label_not_entered")}</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p>{t("label_not_entered")}</p>
                    )}
                </div>
            </div>
        </main>
    );
}
